import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_BASE_URL || '';
const SEEN_KEY = 'ht_seen_notifs';
const REFRESH_MS = 5 * 60 * 1000;  // re-fetch every 5 min
const CHECK_MS   = 30 * 1000;       // check reminder every 30s
const REMIND_MS  = 6 * 60 * 1000;   // fire when ≤ 6 min away (covers 30s polling gap)

const GROOMING_LABELS = {
  bath:             'Baño',
  haircut:          'Corte',
  'hygiene-cut':    'Corte higiénico',
  deshedding:       'Deslanado',
  'medicated-bath': 'Baño medicado',
  colorimetry:      'Colorimetría',
};

function loadSeen() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); }
  catch { return new Set(); }
}

function saveSeen(set) {
  try {
    const arr = [...set].slice(-300);
    localStorage.setItem(SEEN_KEY, JSON.stringify(arr));
  } catch {}
}

const VACCINE_CAUSES = new Set(['Vacuna', 'Esquema cachorro', 'Esquema adulto']);

function toNotif(appt, type) {
  const dogName =
    appt.dog?.name ||
    appt.manualDogName ||
    appt.walkInInfo?.dogName ||
    'Sin nombre';

  let label;
  if (type === 'grooming') {
    const svc = appt.groomingServices?.[0];
    label = GROOMING_LABELS[svc] || 'Estética';
    if (appt.groomingServices?.length > 1) label += ` +${appt.groomingServices.length - 1}`;
  } else {
    label = appt.cause || 'Consulta veterinaria';
    if (appt.vaccines?.length > 0) label += `: ${appt.vaccines.join(', ')}`;
  }

  const isVaccine = type === 'vet' && VACCINE_CAUSES.has(appt.cause);

  return {
    id:      appt._id,
    type,
    icon:    type === 'grooming' ? '✂️' : isVaccine ? '💉' : '🩺',
    dogName,
    label,
    time:    new Date(appt.appointmentDate),
    url:     type === 'grooming' ? '/grooming' : '/vet',
  };
}

export function useNotifications() {
  const { authorizedFetch } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [seenIds, setSeenIds]   = useState(loadSeen);
  const [reminder, setReminder] = useState(null);
  const remindedRef = useRef(new Set());

  const load = useCallback(async () => {
    if (!authorizedFetch) return;
    try {
      const [gRes, vRes, bRes] = await Promise.all([
        authorizedFetch(`${API_URL}/api/grooming`),
        authorizedFetch(`${API_URL}/api/vet/appointments`),
        authorizedFetch(`${API_URL}/api/dogs/birthdays/today`),
      ]);
      const gData = gRes.ok ? await gRes.json() : [];
      const vData = vRes.ok ? await vRes.json() : [];
      const bData = bRes.ok ? await bRes.json() : [];

      const grooming  = Array.isArray(gData) ? gData : (gData.bookings || []);
      const vet       = Array.isArray(vData) ? vData : (vData.appointments || []);
      const birthdays = Array.isArray(bData) ? bData : [];

      const now        = Date.now();
      const endOfToday = new Date(); endOfToday.setHours(23, 59, 59, 999);
      const cutoff     = endOfToday.getTime();

      const today = new Date();
      const birthdayNotifs = birthdays.map(dog => {
        const dob  = new Date(dog.dateOfBirth);
        const age  = today.getFullYear() - dob.getFullYear();
        return {
          id:      `${dog._id}-bday-${today.getFullYear()}`,
          type:    'birthday',
          icon:    '🎂',
          dogName: dog.name,
          label:   `Cumpleaños · ${age} año${age !== 1 ? 's' : ''}`,
          time:    null,
          url:     '/dogs',
        };
      });

      const apptNotifs = [
        ...grooming
          .filter(a => a.status === 'scheduled' || a.status === 'in-progress')
          .map(a => toNotif(a, 'grooming')),
        ...vet
          .filter(a => a.status === 'scheduled' || a.status === 'in-progress')
          .map(a => toNotif(a, 'vet')),
      ].filter(n => { const t = n.time.getTime(); return t >= now && t <= cutoff; })
       .sort((a, b) => a.time - b.time);

      setNotifications([...birthdayNotifs, ...apptNotifs]);
    } catch {}
  }, [authorizedFetch]);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  // 5-min reminder check
  useEffect(() => {
    const check = () => {
      const now = Date.now();
      for (const n of notifications) {
        if (!n.time) continue; // birthdays have no specific time — skip reminder
        const diff = n.time.getTime() - now;
        if (diff > 0 && diff <= REMIND_MS && !remindedRef.current.has(n.id)) {
          remindedRef.current.add(n.id);
          setReminder(n);
          break;
        }
      }
    };
    check();
    const t = setInterval(check, CHECK_MS);
    return () => clearInterval(t);
  }, [notifications]);

  const unreadCount = notifications.filter(n => !seenIds.has(n.id)).length;

  const markAllRead = useCallback(() => {
    setSeenIds(prev => {
      const next = new Set([...prev, ...notifications.map(n => n.id)]);
      saveSeen(next);
      return next;
    });
  }, [notifications]);

  const dismissReminder = useCallback(() => setReminder(null), []);

  return { notifications, unreadCount, markAllRead, reminder, dismissReminder };
}
